import React, { useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MatriculaPlanTab({ userData, refreshData }) {
  const { getToken } = useAdminAuth();
  const { user, enrollment, scholarship, summary, activity } = userData;
  
  const [showCohortModal, setShowCohortModal] = useState(false);
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Build timeline from activity data
  const buildTimeline = () => {
    if (!activity || activity.length === 0) {
      return [
        { type: 'registered', label: 'Registro', date: enrollment?.created_at, completed: true }
      ];
    }

    const timeline = [];
    
    // Registration
    const registration = activity.find(a => a.action === 'create' || a.type === 'registered');
    timeline.push({
      type: 'registered',
      label: 'Registro Completo',
      date: registration?.timestamp || enrollment?.created_at,
      completed: true,
      icon: '✓'
    });

    // Scholarship
    const scholarshipApplied = activity.find(a => a.action === 'scholarship_applied' || a.resource_type === 'scholarship');
    if (summary.has_scholarship) {
      timeline.push({
        type: 'scholarship',
        label: `Beca ${summary.scholarship_percentage}% Aplicada`,
        date: scholarshipApplied?.timestamp,
        completed: true,
        icon: '🎓'
      });
    }

    // Checkout
    const checkoutCreated = activity.find(a => a.action === 'checkout_created');
    if (checkoutCreated || summary.total_paid > 0 || enrollment?.status === 'active') {
      timeline.push({
        type: 'checkout',
        label: 'Checkout Iniciado',
        date: checkoutCreated?.timestamp,
        completed: true,
        icon: '💳'
      });
    }

    // Payment
    if (summary.total_paid > 0) {
      const paymentCompleted = activity.find(a => a.action === 'payment_completed');
      timeline.push({
        type: 'payment',
        label: `Pago Recibido ($${summary.total_paid})`,
        date: paymentCompleted?.timestamp,
        completed: true,
        icon: '✓'
      });
    }

    // Activation
    if (enrollment?.status === 'active') {
      const activation = activity.find(a => a.action === 'activated');
      timeline.push({
        type: 'active',
        label: 'Cuenta Activada',
        date: activation?.timestamp || enrollment?.activated_at,
        completed: true,
        icon: '🚀'
      });
    } else if (enrollment?.status === 'suspended') {
      timeline.push({
        type: 'suspended',
        label: 'Cuenta Suspendida',
        date: enrollment?.suspended_at,
        completed: true,
        icon: '⚠️'
      });
    }

    return timeline;
  };

  const timeline = buildTimeline();

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setShowCohortModal(true)}
          className="px-4 py-2 bg-[#1a4d5c]/30 hover:bg-[#1a4d5c]/50 text-white rounded-lg transition-colors border border-[#1a4d5c]/50 text-sm"
        >
          📚 Cambiar Cohorte
        </button>
        <button
          onClick={() => setShowScholarshipModal(true)}
          className="px-4 py-2 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] rounded-lg transition-colors border border-[#D4AF37]/50 text-sm"
        >
          🎓 Modificar Beca
        </button>
        <button
          onClick={() => setShowPaymentLinkModal(true)}
          className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors border border-blue-500/50 text-sm"
        >
          💳 Generar Link Pago
        </button>
        <button
          onClick={() => setShowManualPaymentModal(true)}
          className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors border border-green-500/50 text-sm"
        >
          💰 Registrar Pago Manual
        </button>
      </div>

      {enrollment ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enrollment Details */}
            <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
              <h4 className="font-semibold text-white mb-4">Detalles de Matrícula</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">ID Enrollment:</span>
                  <span className="text-white font-mono text-sm">{enrollment.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Cohorte:</span>
                  <span className="text-white">{enrollment.cohort_id || 'Sin asignar'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Plan:</span>
                  <span className="text-white capitalize">{enrollment.plan || 'full'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    enrollment.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    enrollment.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{enrollment.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Fecha Registro:</span>
                  <span className="text-white text-sm">
                    {enrollment.created_at ? new Date(enrollment.created_at).toLocaleDateString('es-ES') : 'N/A'}
                  </span>
                </div>
                {enrollment.activated_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fecha Activación:</span>
                    <span className="text-white text-sm">{new Date(enrollment.activated_at).toLocaleDateString('es-ES')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Details */}
            <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
              <h4 className="font-semibold text-white mb-4">Detalles Financieros</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Valor Programa:</span>
                  <span className="text-white font-bold">$7,500</span>
                </div>
                
                {summary.has_scholarship && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Beca Aplicada:</span>
                      <span className="text-[#D4AF37] font-bold">{summary.scholarship_percentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Descuento:</span>
                      <span className="text-green-400">-${(7500 * summary.scholarship_percentage / 100).toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between pt-3 border-t border-[#1a4d5c]/30">
                  <span className="text-gray-400">Balance:</span>
                  <span className="text-white font-bold text-xl">${summary.balance}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Pagado:</span>
                  <span className="text-green-400 font-bold text-xl">${summary.total_paid}</span>
                </div>
                
                {enrollment.can_split_in_3 && (
                  <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                    <p className="text-blue-400 text-sm">✓ Elegible para pago en 3 cuotas</p>
                  </div>
                )}

                {enrollment.enrollment_fee_cents && (
                  <div className="mt-2 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <div className="text-purple-400 text-sm">Inscripción: ${(enrollment.enrollment_fee_cents / 100).toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Scholarship Details */}
          {scholarship && (
            <div className="bg-[#D4AF37]/10 rounded-lg p-6 border border-[#D4AF37]/30">
              <h4 className="font-semibold text-[#D4AF37] mb-4">Detalles de Beca</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-gray-400 text-sm">Código</div>
                  <div className="text-white font-mono font-bold">{scholarship.code}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Porcentaje</div>
                  <div className="text-[#D4AF37] font-bold text-xl">{scholarship.percentage}%</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Usos</div>
                  <div className="text-white">{scholarship.current_uses} / {scholarship.max_uses || '∞'}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Válido hasta</div>
                  <div className="text-white">
                    {scholarship.valid_until ? new Date(scholarship.valid_until).toLocaleDateString('es-ES') : 'Permanente'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-black/30 rounded-lg p-6 border border-[#1a4d5c]/30">
            <h4 className="font-semibold text-white mb-6">Timeline de Matrícula</h4>
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#1a4d5c]/30"></div>
              
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <div key={index} className="relative flex items-start gap-4">
                    {/* Icon */}
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                      item.completed 
                        ? 'bg-green-500/20 border-green-500/50' 
                        : 'bg-gray-500/20 border-gray-500/50'
                    }`}>
                      <span className="text-lg">{item.icon || '•'}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-2">
                      <div className="font-semibold text-white">{item.label}</div>
                      {item.date && (
                        <div className="text-sm text-gray-400 mt-1">
                          {new Date(item.date).toLocaleString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-400">No hay información de matrícula</p>
        </div>
      )}

      {/* Modals */}
      {showCohortModal && (
        <ChangeCohortModal
          userId={user.id}
          currentCohort={enrollment?.cohort_id}
          onClose={() => setShowCohortModal(false)}
          onSuccess={() => {
            setShowCohortModal(false);
            refreshData();
          }}
        />
      )}

      {showScholarshipModal && (
        <ModifyScholarshipModal
          userId={user.id}
          currentScholarship={scholarship}
          onClose={() => setShowScholarshipModal(false)}
          onSuccess={() => {
            setShowScholarshipModal(false);
            refreshData();
          }}
        />
      )}

      {showPaymentLinkModal && (
        <GeneratePaymentLinkModal
          userId={user.id}
          balance={summary.balance}
          canSplitIn3={enrollment?.can_split_in_3}
          onClose={() => setShowPaymentLinkModal(false)}
          onSuccess={() => {
            setShowPaymentLinkModal(false);
            refreshData();
          }}
        />
      )}

      {showManualPaymentModal && (
        <ManualPaymentModal
          userId={user.id}
          onClose={() => setShowManualPaymentModal(false)}
          onSuccess={() => {
            setShowManualPaymentModal(false);
            refreshData();
          }}
        />
      )}
    </div>
  );
}

// Change Cohort Modal
function ChangeCohortModal({ userId, currentCohort, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [cohortId, setCohortId] = useState(currentCohort || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cohortId.trim()) {
      alert('Por favor ingresa un ID de cohorte');
      return;
    }

    setIsLoading(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${userId}/enrollment`,
        { cohort_id: cohortId },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Cohorte actualizado correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al cambiar cohorte');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Cambiar Cohorte</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">ID de Cohorte</label>
            <input
              type="text"
              value={cohortId}
              onChange={(e) => setCohortId(e.target.value)}
              placeholder="cohort_oct_2025"
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">Ejemplo: cohort_oct_2025, cohort_nov_2025</p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Cambiar Cohorte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modify Scholarship Modal
function ModifyScholarshipModal({ userId, currentScholarship, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [code, setCode] = useState(currentScholarship?.code || '');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      alert('Por favor ingresa un código de beca');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/scholarship`,
        { code: code.toUpperCase(), reason },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Beca actualizada correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al aplicar beca');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Modificar Beca</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Código de Beca</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="URPE80"
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Motivo (opcional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows="3"
              placeholder="Razón para cambiar la beca..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Aplicando...' : 'Aplicar Beca'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Generate Payment Link Modal
function GeneratePaymentLinkModal({ userId, balance, canSplitIn3, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [type, setType] = useState('full');
  const [installments, setInstallments] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/payment-link`,
        { type, installments: parseInt(installments) },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Copy to clipboard
      if (response.data.checkout_url) {
        navigator.clipboard.writeText(response.data.checkout_url);
        alert(`Link generado y copiado al portapapeles:\n\n${response.data.checkout_url}`);
      }
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Generar Link de Pago</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Tipo de Pago</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="full">Pago Completo (${balance})</option>
              {canSplitIn3 && <option value="installment">Pago en Cuotas</option>}
              <option value="enrollment_fee">Inscripción ($497)</option>
            </select>
          </div>
          
          {type === 'installment' && canSplitIn3 && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Número de Cuotas</label>
              <select
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="1">1 cuota</option>
                <option value="3">3 cuotas</option>
              </select>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Generando...' : 'Generar Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Manual Payment Modal
function ManualPaymentModal({ userId, onClose, onSuccess }) {
  const { getToken } = useAdminAuth();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      alert('Por favor ingresa un monto válido');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/manual-payment`,
        {
          amount: parseFloat(amount),
          payment_method: method,
          reference,
          notes
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      alert('Pago manual registrado correctamente');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="bg-[#0A0A0A] rounded-lg max-w-md w-full p-6 border border-[#1a4d5c]/30">
        <h3 className="text-xl font-bold text-white mb-4">Registrar Pago Manual</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Monto (USD)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1500.00"
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Método de Pago</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="bank_transfer">Transferencia Bancaria</option>
              <option value="wire_transfer">Wire Transfer</option>
              <option value="cash">Efectivo</option>
              <option value="check">Cheque</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Referencia/Comprobante</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="REF-12345"
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="2"
              placeholder="Información adicional sobre el pago..."
              className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] disabled:opacity-50"
            >
              {isLoading ? 'Registrando...' : 'Registrar Pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}